import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setValidating, updateUser, logout } from '@/store/authSlice';
import { authApi } from '@/api/auth';

/**
 * Validates the stored session on app load.
 * If session is invalid/expired, logs out the user.
 * If valid, updates user data from server.
 *
 * `enabled: false` skips validation entirely — used on public pages (the
 * payment-status share page) that must never call authenticated endpoints.
 */
export function useSessionValidator({ enabled = true }: { enabled?: boolean } = {}) {
  const dispatch = useAppDispatch();
  const sessionToken = useAppSelector((state) => state.auth.session?.sessionToken);
  const sessionExpiresAt = useAppSelector((state) => state.auth.session?.expiresAt);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isValidating = useAppSelector((state) => state.auth.isValidating);
  const hasValidated = useRef(false);
  const lastSessionToken = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    // Only validate once per app load, and only if we have a stored session
    if (!isAuthenticated || !sessionToken) {
      // Reset when logged out so re-login triggers validation
      hasValidated.current = false;
      lastSessionToken.current = null;
      return;
    }

    // Skip if already validated for this exact session token
    if (hasValidated.current && lastSessionToken.current === sessionToken) {
      return;
    }

    hasValidated.current = true;
    lastSessionToken.current = sessionToken;

    const validateSession = async () => {
      dispatch(setValidating(true));

      try {
        // Check if session has expired locally first
        if (sessionExpiresAt) {
          const expiresAt = new Date(sessionExpiresAt).getTime();
          if (Date.now() > expiresAt) {
            dispatch(logout());
            return;
          }
        }

        // Validate with server
        const result = await authApi.getSession();

        // Update user data from server (might have changed)
        dispatch(updateUser(result.user));
      } catch {
        // Session invalid on server - log out
        dispatch(logout());
      }
    };

    void validateSession();
  }, [dispatch, enabled, isAuthenticated, sessionToken, sessionExpiresAt]);

  return { isValidating };
}

export default useSessionValidator;
